import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Img } from "@/components/ui/img";
import { blogCardExcerpt } from "@/lib/blog-excerpt";
import {
  CheckCircle,
  XCircle,
  Eye,
  Pencil,
  Trash,
  User,
  CalendarDots,
} from "@phosphor-icons/react";
import { PostStatusBadge } from "./PostStatusBadge";
import { formatBlogDate } from "./utils";
import type { BlogPost } from "@/types";

export function BlogPostCard({
  post,
  onPreview,
  onReject,
  onApprove,
  onDelete,
  isApproving,
}: {
  post: BlogPost;
  onPreview: () => void;
  onReject: () => void;
  onApprove: () => void;
  onDelete: () => void;
  isApproving: boolean;
}) {
  const excerpt = blogCardExcerpt(post);

  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm transition-all duration-200 hover:shadow-md sm:flex-row">
      {/* Cover image */}
      {post.coverImageUrl && (
        <div className="relative h-36 shrink-0 overflow-hidden sm:h-auto sm:w-44">
          <Img
            src={post.coverImageUrl}
            alt=""
            className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3
              className="line-clamp-1 text-base font-semibold text-foreground sm:text-lg"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {post.title}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <User className="size-3.5" />
                {post.authorName || "Nepoznat"}
              </span>
              <span className="inline-flex items-center gap-1">
                <CalendarDots className="size-3.5" />
                {formatBlogDate(post.publishedAt || post.createdAt)}
              </span>
            </div>
          </div>
          <PostStatusBadge status={post.status} />
        </div>

        {excerpt && (
          <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
            {excerpt}
          </p>
        )}

        {/* Actions */}
        <div className="mt-auto flex items-center justify-between gap-2 pt-3">
          {post.status === "pending" ? (
            <div className="flex gap-1.5">
              <Button
                size="sm"
                onClick={onApprove}
                disabled={isApproving}
                className="h-8"
              >
                <CheckCircle className="size-3.5" />
                Odobri
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={onReject}
                className="h-8"
              >
                <XCircle className="size-3.5" />
                Odbij
              </Button>
            </div>
          ) : (
            <div />
          )}

          <div className="flex gap-1">
            <button
              onClick={onPreview}
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Pregled"
            >
              <Eye className="size-4" />
            </button>
            <Link
              to={`/dashboard/admin/blog/${post.id}/edit`}
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Uredi"
            >
              <Pencil className="size-4" />
            </Link>
            <button
              onClick={onDelete}
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600"
              aria-label="Obriši"
            >
              <Trash className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
